import React from 'react';
import { SortableContainer } from './drag-and-drop';
import { Row, HeaderCellContainer } from './components';
import { useTableManager } from './hooks';
import PropTypes from 'prop-types';
import './index.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SortableList = SortableContainer(({
  forwardRef,
  className,
  style,
  children
}) => /*#__PURE__*/_jsx("div", {
  ref: forwardRef,
  className: className,
  style: style,
  children: children
}));
const GridTablePropTypes = {
  // general
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  selectedRowsIds: PropTypes.array,
  searchText: PropTypes.string,
  getIsRowSelectable: PropTypes.func,
  getIsRowEditable: PropTypes.func,
  editRowId: PropTypes.any,
  // table config
  rowIdField: PropTypes.string,
  batchSize: PropTypes.number,
  isPaginated: PropTypes.bool,
  enableColumnsReorder: PropTypes.bool,
  pageSizes: PropTypes.arrayOf(PropTypes.number),
  pageSize: PropTypes.number,
  page: PropTypes.number,
  sort: PropTypes.object,
  minColumnResizeWidth: PropTypes.number,
  highlightSearch: PropTypes.bool,
  showSearch: PropTypes.bool,
  showRowsInformation: PropTypes.bool,
  showColumnVisibilityManager: PropTypes.bool,
  minSearchChars: PropTypes.number,
  isLoading: PropTypes.bool,
  isHeaderSticky: PropTypes.bool,
  isVirtualScroll: PropTypes.bool,
  icons: PropTypes.object,
  texts: PropTypes.object,
  additionalProps: PropTypes.object,
  components: PropTypes.object,
  totalRows: PropTypes.number,
  requestDebounceTimeout: PropTypes.number,
  selectAllMode: PropTypes.string,
  // events
  onColumnsChange: PropTypes.func,
  onSearchTextChange: PropTypes.func,
  onSelectedRowsChange: PropTypes.func,
  onSortChange: PropTypes.func,
  onRowClick: PropTypes.func,
  onEditRowIdChange: PropTypes.func,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  onLoad: PropTypes.func,
  onColumnResizeStart: PropTypes.func,
  onColumnResize: PropTypes.func,
  onColumnResizeEnd: PropTypes.func,
  onColumnReorderStart: PropTypes.func,
  onColumnReorderEnd: PropTypes.func,
  onRowsRequest: PropTypes.func,
  onRowsReset: PropTypes.func,
  onRowsChange: PropTypes.func,
  onTotalRowsChange: PropTypes.func
};

const GridTable = props => {
  const tableManager = useTableManager(props);
  const {
    id,
    isLoading,
    config: {
      isVirtualScroll,
      rowIdField,
      components: {
        Header,
        Footer,
        Loader,
        NoResults,
        DragHandle,
        ContextMenu
      }
    },
    refs: {
      rgtRef,
      tableRef
    },
    columnsApi: {
      visibleColumns
    },
    columnsReorderApi: {
      onColumnReorderStart,
      onColumnReorderEnd
    },
    rowVirtualizer: {
      virtualItems
    },
    paginationApi: {
      pageRows
    },
    rowsApi: {
      totalRows
    },
    rowSelectionApi: {
      selectedRowsIds
    }
  } = tableManager;
  const rest = Object.keys(props).reduce((rest, key) => {
    if (GridTablePropTypes[key] === undefined) rest = { ...rest,
      [key]: props[key]
    };
    return rest;
  }, {});
  const classNames = ('rgt-wrapper ' + (props.className || '')).trim();
  const showContextMenu = typeof ContextMenu !== 'undefined' && selectedRowsIds.length > 0;
  return /*#__PURE__*/_jsxs("div", { ...rest,
    ref: rgtRef,
    id: id,
    className: classNames,
    children: [showContextMenu && /*#__PURE__*/_jsx(ContextMenu, {
      tableManager: tableManager
    }), /*#__PURE__*/_jsx(Header, {
      tableManager: tableManager
    }), /*#__PURE__*/_jsxs(SortableList, {
      forwardRef: tableRef,
      getContainer: () => tableRef,
      className: "rgt-container",
      axis: "x",
      lockToContainerEdges: true,
      distance: 10,
      lockAxis: "x",
      useDragHandle: !!DragHandle,
      onSortStart: onColumnReorderStart,
      onSortEnd: onColumnReorderEnd,
      style: {
        display: 'grid',
        overflow: 'auto',
        flex: 1,
        gridTemplateColumns: visibleColumns.map(column => column.width).join(' '),
        gridTemplateRows: `repeat(${pageRows.length + 1 + (isVirtualScroll ? 1 : 0)}, max-content)`
      },
      children: [!showContextMenu && visibleColumns.map((visibleColumn, idx) => /*#__PURE__*/_jsx(HeaderCellContainer, {
        index: idx,
        column: visibleColumn,
        tableManager: tableManager
      }, visibleColumn.id)), totalRows && visibleColumns.length > 1 ? isVirtualScroll ? [/*#__PURE__*/_jsx(Row, {
        index: 'virtual-start',
        tableManager: tableManager
      }, 'virtual-start'), ...virtualItems.map(virtualizedRow => /*#__PURE__*/_jsx(Row, {
        index: virtualizedRow.index,
        data: pageRows[virtualizedRow.index],
        measureRef: virtualizedRow.measureRef,
        tableManager: tableManager
      }, virtualizedRow.index)), /*#__PURE__*/_jsx(Row, {
        index: 'virtual-end',
        tableManager: tableManager
      }, 'virtual-end')] : pageRows.map((rowData, index) => /*#__PURE__*/_jsx(Row, {
        index: index,
        data: rowData,
        tableManager: tableManager
      }, rowData == null ? void 0 : rowData[rowIdField])) : null]
    }), !totalRows || !visibleColumns.length ? /*#__PURE__*/_jsx("div", {
      className: "rgt-container-overlay",
      children: isLoading ? /*#__PURE__*/_jsx(Loader, {
        tableManager: tableManager
      }) : /*#__PURE__*/_jsx(NoResults, {
        tableManager: tableManager
      })
    }) : null, /*#__PURE__*/_jsx(Footer, {
      tableManager: tableManager
    })]
  });
};

GridTable.defaultProps = {
  columns: [],
  rowIdField: 'id',
  minColumnResizeWidth: 70,
  pageSizes: [10, 20, 50, 100],
  isHeaderSticky: true,
  highlightSearch: true,
  minSearchChars: 2,
  isPaginated: true,
  isVirtualScroll: true,
  showSearch: false,
  showRowsInformation: true,
  showColumnVisibilityManager: false,
  enableColumnsReorder: true,
  requestDebounceTimeout: 300,
  getIsRowSelectable: () => true,
  getIsRowEditable: () => true,
  selectAllMode: 'page' // ['page', 'all']

};
export default GridTable;
export * from './components';
export * from './hooks';